import type { WeatherDaily, WeatherHourly } from "@weather-deck/shared";
import { el } from "./dom";

function getWeatherInfo(code?: number): { icon: string; label: string } {
  if (code == null) return { icon: "🌡️", label: "Fair" };
  if (code === 0) return { icon: "☀️", label: "Clear Sky" };
  if (code >= 1 && code <= 3) return { icon: "⛅", label: "Partly Cloudy" };
  if (code >= 45 && code <= 48) return { icon: "🌫️", label: "Foggy" };
  if (code >= 51 && code <= 55) return { icon: "🌦️", label: "Light Drizzle" };
  if (code >= 61 && code <= 65) return { icon: "🌧️", label: "Rain" };
  if (code >= 71 && code <= 77) return { icon: "🌨️", label: "Snow" };
  if (code >= 80 && code <= 82) return { icon: "🌧️", label: "Rain Showers" };
  if (code >= 85 && code <= 86) return { icon: "🌨️", label: "Snow Showers" };
  if (code >= 95) return { icon: "⛈️", label: "Thunderstorm" };
  return { icon: "🌤️", label: "Fair" };
}

function formatDayName(dateStr: string, index: number): string {
  if (index === 0) return "Today";
  if (index === 1) return "Tomorrow";
  const dt = new Date(dateStr);
  return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function formatTime(isoStr?: string): string {
  if (!isoStr) return "";
  const dt = new Date(isoStr);
  return dt.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Hours belonging to one forecast day.
 *
 * Open-Meteo is queried with `timezone=Europe/Berlin`, so it returns local
 * wall-clock stamps with no offset ("2026-08-17T14:00") and daily dates as
 * plain "2026-08-17". Matching on the date prefix is therefore exact — parsing
 * these into Date objects would reintroduce the UTC-shift bug the string form
 * avoids.
 */
export function hoursForDay(hourly: WeatherHourly[], date: string): WeatherHourly[] {
  return hourly.filter((h) => h.time.slice(0, 10) === date);
}

/** "14:00" straight off the local stamp — no Date round-trip. */
function hourLabel(time: string): string {
  return time.slice(11, 16);
}

function buildHourRow(h: WeatherHourly, isPast: boolean): HTMLElement {
  const { icon } = getWeatherInfo(h.weatherCode);
  const row = el("div", { className: isPast ? "hour-row is-past" : "hour-row" }, [
    el("span", { className: "hour-time", text: hourLabel(h.time) }),
    el("span", { className: "hour-icon", text: icon }),
    el("span", { className: "hour-temp", text: `${Math.round(h.temperature)}°` }),
    el("span", {
      className: h.precipitationProbability >= 40 ? "hour-rain is-likely" : "hour-rain",
      text: `${Math.round(h.precipitationProbability)}%`
    }),
    el("span", {
      className: "hour-mm",
      text: h.precipitation > 0 ? h.precipitation.toFixed(1) : "—"
    }),
    el("span", { className: "hour-wind", text: `${Math.round(h.windSpeed)}` }),
    el("span", { className: "hour-uv", text: h.uv != null ? `${Math.round(h.uv)}` : "—" })
  ]);
  return row;
}

function buildHourTable(hours: WeatherHourly[], nowHour: string | null): HTMLElement {
  const head = el("div", { className: "hour-row hour-head" }, [
    el("span", { className: "hour-time", text: "Time" }),
    el("span", { className: "hour-icon", text: "" }),
    el("span", { className: "hour-temp", text: "°C" }),
    el("span", { className: "hour-rain", text: "Rain" }),
    el("span", { className: "hour-mm", text: "mm" }),
    el("span", { className: "hour-wind", text: "km/h" }),
    el("span", { className: "hour-uv", text: "UV" })
  ]);
  const rows = hours.map((h) => buildHourRow(h, nowHour != null && h.time < nowHour));
  return el("div", { className: "hour-table" }, [head, ...rows]);
}

/**
 * `now` is only used to dim hours of today that have already passed — the rest
 * of the card is pure formatting, so tests can pin a fixed instant.
 */
export function buildDailyForecast(
  dailyList: WeatherDaily[],
  hourly: WeatherHourly[] = [],
  now: Date = new Date()
): HTMLElement {
  if (!dailyList || dailyList.length === 0) {
    return el("section", { className: "daily-forecast" }, [
      el("h3", { text: "7-Day Weather Forecast" }),
      el("p", { text: "No forecast data available." })
    ]);
  }

  // Local wall-clock stamp in the same shape as hourly.time, so "already past"
  // is a string comparison against the same clock Open-Meteo returned.
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false
  }).format(now);
  const nowHour = `${parts.slice(0, 10)}T${parts.slice(11, 16)}`;

  const cards = dailyList.map((day, idx) => {
    const { icon, label } = getWeatherInfo(day.weatherCode);
    const dayTitle = formatDayName(day.date, idx);
    const tempRange = `${day.tMin.toFixed(0)}° / ${day.tMax.toFixed(0)}°`;
    const rainProb = day.precipProbMax != null ? `${day.precipProbMax}%` : "0%";
    const rainSum = day.precipSum != null && day.precipSum > 0 ? ` (${day.precipSum.toFixed(1)} mm)` : "";
    const sunriseStr = formatTime(day.sunrise);
    const sunsetStr = formatTime(day.sunset);
    const hours = hoursForDay(hourly, day.date);

    const summaryBody = el("div", { className: "daily-card-body" }, [
      el("div", { className: "day-header" }, [
        el("span", { className: "day-name", text: dayTitle }),
        el("span", { className: "weather-icon", text: icon })
      ]),
      el("div", { className: "weather-label", text: label }),
      el("div", { className: "temp-range", text: tempRange }),
      el("div", { className: "details-row" }, [
        el("span", { text: `💧 ${rainProb}${rainSum}` }),
        day.uvMax != null ? el("span", { text: `☀️ UV ${day.uvMax.toFixed(0)}` }) : document.createTextNode("")
      ]),
      sunriseStr && sunsetStr ? el("div", { className: "sun-row", text: `🌅 ${sunriseStr}  🌇 ${sunsetStr}` }) : document.createTextNode(""),
      el("div", {
        className: "expand-hint",
        text: hours.length > 0 ? `${hours.length} hours — tap for detail` : "hourly detail unavailable"
      })
    ]);

    // A day with no hourly rows stays a plain card rather than an expander that
    // opens onto nothing.
    if (hours.length === 0) {
      const card = el("div", { className: "daily-card" }, [summaryBody]);
      return card;
    }

    const details = el("details", { className: "daily-card is-expandable" });
    const summary = el("summary", { className: "daily-card-summary" }, [summaryBody]);
    details.append(summary, buildHourTable(hours, day.date === nowHour.slice(0, 10) ? nowHour : null));
    return details;
  });

  return el("section", { className: "daily-forecast" }, [
    el("h3", { text: "7-Day Detailed Forecast" }),
    el("div", { className: "daily-grid" }, cards)
  ]);
}
